import React from 'react';
import { cn } from 'rizzui';

interface ScorePeriodCardProps {
    title: string;
    currentScore: number;
    previousScore: number;
    isLoading?: boolean;
}

const ScorePeriodCard: React.FC<ScorePeriodCardProps> = ({
    title,
    currentScore,
    previousScore,
    isLoading = false
}) => {
    // Calculate percentage variation and determine if it's positive or negative
    const calculateVariation = (): { percentage: number; isPositive: boolean } => {
        if (Number(previousScore) === 0) {
            return { percentage: Number(currentScore) > 0 ? 100 : 0, isPositive: Number(currentScore) > 0 };
        } else {
            const variation = ((Number(currentScore) - Number(previousScore)) / Number(previousScore)) * 100;
            return { percentage: Math.abs(variation), isPositive: variation >= 0 };
        }
    };

    const { percentage, isPositive } = calculateVariation();

    if (isLoading) {
        return (
            <div className="bg-white rounded-lg border border-muted p-4 animate-pulse">
                <div className="h-4 bg-gray-200 rounded mb-2"></div>
                <div className="h-8 bg-gray-200 rounded mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-lg border border-muted p-4 hover:shadow-md transition-shadow">
            <div className="flex flex-col">
                {/* Title */}
                <h3 className="text-sm font-medium text-gray-600 mb-2">
                    {title}
                </h3>

                {/* Current score */}
                <div className="flex items-baseline justify-between mb-2">
                    <span className="text-2xl font-bold text-gray-900">
                        {Number(currentScore).toFixed(2)}
                    </span>
                    <span className="text-sm text-gray-500">
                        puntos
                    </span>
                </div>

                {/* Percentage variation */}
                <div className="flex items-center justify-between">
                    <div className={cn(
                        "flex items-center text-sm font-medium",
                        isPositive ? "text-green-600" : (Number(percentage) === 0 ? "text-gray-500" : "text-red-600")
                    )}>
                        <span className="mr-1">
                            {isPositive ? "↗" : (Number(percentage) === 0 ? "→" : "↘")}
                        </span>
                        <span>
                            {percentage.toFixed(2)}%
                        </span>
                    </div>

                    {/* Previous score */}
                    <span className="text-xs text-gray-400">
                        vs {Number(previousScore).toFixed(2)}
                    </span>
                </div>

                {/* Trend visual indicator */}
                <div className="mt-3 h-1 bg-gray-100 rounded-full overflow-hidden">
                    <div
                        className={cn(
                            "h-full transition-all duration-300",
                            isPositive ? "bg-green-500" : "bg-red-500"
                        )}
                        style={{
                            width: `${Math.min(percentage, 100)}%`
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default ScorePeriodCard;